/**
 * Done. / ChiroCPD — Input Primitive
 *
 * WCAG 2.1 AA: always paired with a visible label, error state announced via
 * aria-describedby, min 44px touch target height.
 */

import { cx } from '@/lib/utils';

interface InputProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'number' | 'date' | 'tel' | 'password';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  errorId?: string | undefined;
  hintId?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  min?: string | number | undefined;
  max?: string | number | undefined;
  step?: string | number | undefined;
  autoComplete?: string | undefined;
  className?: string | undefined;
}

export function Input({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  errorId,
  hintId,
  hint,
  error,
  min,
  max,
  step,
  autoComplete,
  className,
}: InputProps): React.ReactElement {
  const computedErrorId = errorId ?? (error ? `${id}-error` : undefined);
  const computedHintId = hintId ?? (hint ? `${id}-hint` : undefined);
  const describedBy = [computedErrorId, computedHintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label
        htmlFor={id}
        className="text-sm font-medium text-[var(--text-primary)]"
      >
        {label}
        {required && (
          <span className="text-[var(--color-error-500)] ml-1" aria-hidden="true">*</span>
        )}
      </label>

      {hint && (
        <p id={computedHintId} className="text-xs text-[var(--text-muted)]">
          {hint}
        </p>
      )}

      <input
        id={id}
        type={type}
        value={value}
        onChange={e => { onChange(e.target.value); }}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        aria-required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete}
        className={cx(
          'w-full rounded-[var(--radius-md)] border px-3 py-2 text-sm',
          'min-h-[44px] bg-[var(--surface-base)] text-[var(--text-primary)]',
          'placeholder:text-[var(--text-muted)]',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] focus:ring-offset-1',
          error
            ? 'border-[var(--color-error-500)] focus:ring-[var(--color-error-500)]'
            : 'border-[var(--border-base)] hover:border-[var(--border-strong)]',
          disabled && 'opacity-50 cursor-not-allowed bg-[var(--surface-muted)]',
        )}
      />

      {error && (
        <p
          id={computedErrorId}
          role="alert"
          className="text-xs text-[var(--color-error-500)]"
        >
          {error}
        </p>
      )}
    </div>
  );
}
